import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import type { PairListResponseDto, PairSummaryDto } from '@rhcst/shared';
import { api, ApiRequestError } from '../lib/api.js';
import { PairRow, PairSymbols, StatusDots, baseTokenOf } from '../components/PairRow.js';
import { SafetyChips } from '../components/SafetyChip.js';

import { formatEth, relativeAge } from '../lib/format.js';

/**
 * Launches and liquidity.
 *
 * Renamed from "discovered pairs" deliberately. The feed carries two different
 * kinds of thing — a launchpad saying it created a token, and a pool merely
 * appearing — and calling them all "pairs" was what let the second be read as
 * the first. See ADR-060.
 *
 * Newest first, with keyset pagination so a pair cannot be skipped while new
 * ones arrive at the head.
 *
 * Filters live in component state rather than the URL. Shareable filtered URLs
 * would be nice, but there is one operator and no one to share with.
 *
 * The redesign addresses two problems named in docs/DESIGN_HANDOFF.md:
 *
 * §5.1 — the feed did not prioritise. Every row was equally loud, in a list
 * where roughly one pair in fifty is worth a second look and the operator's
 * actual job is triage. The best candidate is now promoted to a featured card
 * above the list.
 *
 * §5.2 — nothing conveyed that the system was live. Pairs arrive continuously
 * and the interface read like a static report. Rows that appeared since the page
 * was opened now flash once on arrival and are counted in the header.
 *
 * Phase 11, Slice 11F adds the tab bar below the header: lifecycle-status
 * triage (Candidates/Watch/Approval Eligible/Rugged/Suspected Rug/Dead
 * Liquidity/Blocked/Stale/All) plus a row of standalone filters that combine
 * with any tab. Slice 11G (component scoring, the gate cascade that computes
 * `candidate`/`approval_eligible`/etc.) has since shipped, so
 * `lifecycle_status=candidates` (plural, the API's own sentinel) now reflects
 * the real gate cascade output for every pair the worker has scored under it,
 * not the interim "not rugged/suspected/dead/blocked/stale" reading this
 * comment described before 11G existed.
 *
 * v2 (docs/design_handoff_console_redesign/) adds a 4-dot row-status legend
 * under the header (lifecycle / approval-eligible / reputation / provenance
 * risk — see `PairRow.tsx`'s `StatusDots` for the tone mapping) and a
 * contract-safety chip on the featured card when the top pick has an
 * ownership-renounce finding worth surfacing.
 */

type StatusFilter = '' | 'new' | 'tracking' | 'ignored' | 'bought' | 'error' | 'rugged';
type QuotedFilter = 'any' | 'known' | 'unknown';
type VerdictFilter = '' | 'avoid' | 'risky' | 'watch' | 'qualified' | 'unscored';

const VERDICT_FILTERS: ReadonlyArray<readonly [VerdictFilter, string]> = [
  ['', 'All'],
  ['qualified', 'Qualified'],
  ['watch', 'Watch'],
  ['risky', 'Risky'],
  ['avoid', 'Avoid'],
  ['unscored', 'Unscored'],
];

/**
 * Lifecycle tabs. Each maps to one or two query params -- never a second
 * query mechanism, per the phase prompt's own instruction for this slice.
 */
type LifecycleTab =
  | 'candidates'
  | 'watch'
  | 'approval_eligible'
  | 'rugged'
  | 'suspected_rug'
  | 'dead_liquidity'
  | 'blocked'
  | 'stale'
  | 'all';

const LIFECYCLE_TABS: ReadonlyArray<readonly [LifecycleTab, string]> = [
  ['candidates', 'Candidates'],
  ['watch', 'Watch'],
  ['approval_eligible', 'Approval eligible'],
  ['rugged', 'Rugged'],
  ['suspected_rug', 'Suspected rug'],
  ['dead_liquidity', 'Dead liquidity'],
  ['blocked', 'Blocked'],
  ['stale', 'Stale'],
  ['all', 'All'],
];

/** Query params a lifecycle tab contributes. `approval_eligible` uses the boolean column, not `lifecycle_status`, since that is the field Slice 11G actually derives approval eligibility onto. */
function lifecycleTabParams(tab: LifecycleTab): Record<string, string> {
  if (tab === 'all') return {};
  if (tab === 'approval_eligible') return { approval_eligible_only: 'true' };
  return { lifecycle_status: tab };
}

interface StandaloneFilters {
  freshLiquidityOnly: boolean;
  watchlistedOnly: boolean;
  highScoreOnly: boolean;
  highLiquidityOnly: boolean;
  highProvenanceRiskOnly: boolean;
  repeatedLpRemoverOnly: boolean;
  newWithin30MinutesOnly: boolean;
}

const STANDALONE_FILTER_TOGGLES: ReadonlyArray<
  readonly [keyof StandaloneFilters, string, string]
> = [
  ['freshLiquidityOnly', 'fresh_liquidity_only', 'Fresh liquidity'],
  ['watchlistedOnly', 'watchlisted_only', 'Watchlisted'],
  ['highScoreOnly', 'high_score_only', 'High score'],
  ['highLiquidityOnly', 'high_liquidity_only', 'High liquidity'],
  ['highProvenanceRiskOnly', 'high_provenance_risk_only', 'High provenance risk'],
  ['repeatedLpRemoverOnly', 'repeated_lp_remover_only', 'Repeated LP remover'],
  ['newWithin30MinutesOnly', 'new_within_30_minutes', 'New (< 30 min)'],
];

const EMPTY_STANDALONE_FILTERS: StandaloneFilters = {
  freshLiquidityOnly: false,
  watchlistedOnly: false,
  highScoreOnly: false,
  highLiquidityOnly: false,
  highProvenanceRiskOnly: false,
  repeatedLpRemoverOnly: false,
  newWithin30MinutesOnly: false,
};

export function Pairs() {
  const [pairs, setPairs] = useState<PairSummaryDto[]>([]);
  const [total, setTotal] = useState(0);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busyPairId, setBusyPairId] = useState<string | null>(null);

  // §5.2 — the ids present on first load. Held in a ref so re-renders do not
  // reset the baseline: "new" must mean "since you opened this", not "since the
  // last render".
  const baseline = useRef<Set<string> | null>(null);
  const [freshIds, setFreshIds] = useState<Set<string>>(new Set());

  const [status, setStatus] = useState<StatusFilter>('');
  const [quoted, setQuoted] = useState<QuotedFilter>('any');
  const [verdict, setVerdict] = useState<VerdictFilter>('');
  const [search, setSearch] = useState('');
  const [lifecycleTab, setLifecycleTab] = useState<LifecycleTab>('candidates');
  const [standaloneFilters, setStandaloneFilters] = useState<StandaloneFilters>(
    EMPTY_STANDALONE_FILTERS,
  );
  // Debounced so typing an address does not fire a request per keystroke.
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    const timer = setTimeout(() => setSearchTerm(search.trim()), 300);
    return () => clearTimeout(timer);
  }, [search]);

  const buildQuery = useCallback(
    (cursor?: string) => {
      const params = new URLSearchParams();
      if (status) params.set('status', status);
      if (quoted !== 'any') params.set('quoted', quoted);
      if (verdict) params.set('verdict', verdict);
      if (searchTerm) params.set('search', searchTerm);
      for (const [key, value] of Object.entries(lifecycleTabParams(lifecycleTab))) {
        params.set(key, value);
      }
      for (const [stateKey, paramKey] of STANDALONE_FILTER_TOGGLES) {
        if (standaloneFilters[stateKey]) params.set(paramKey, 'true');
      }
      params.set('limit', '25');
      if (cursor) params.set('cursor', cursor);
      return params.toString();
    },
    [status, quoted, verdict, searchTerm, lifecycleTab, standaloneFilters],
  );

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await api.get<PairListResponseDto>(`/pairs?${buildQuery()}`);

      if (baseline.current === null) {
        baseline.current = new Set(result.pairs.map((pair) => pair.id));
      } else {
        const known = baseline.current;
        setFreshIds(new Set(result.pairs.filter((p) => !known.has(p.id)).map((p) => p.id)));
      }

      setPairs(result.pairs);
      setTotal(result.total);
      setNextCursor(result.next_cursor);
    } catch (err) {
      if (!(err instanceof ApiRequestError && err.isUnauthorized)) {
        setError('Could not load pairs.');
      }
    } finally {
      setLoading(false);
    }
  }, [buildQuery]);

  useEffect(() => {
    void load();
  }, [load]);

  async function loadMore() {
    if (!nextCursor) return;
    setLoadingMore(true);
    try {
      const result = await api.get<PairListResponseDto>(`/pairs?${buildQuery(nextCursor)}`);
      // Appended by id to guard against a duplicate if a row shifted pages.
      setPairs((current) => {
        const seen = new Set(current.map((pair) => pair.id));
        return [...current, ...result.pairs.filter((pair) => !seen.has(pair.id))];
      });
      setNextCursor(result.next_cursor);
    } catch {
      setError('Could not load more pairs.');
    } finally {
      setLoadingMore(false);
    }
  }

  async function toggleWatch(pair: PairSummaryDto) {
    setBusyPairId(pair.id);

    // Optimistic: the operator gets immediate feedback, and the change is
    // reverted if the request fails rather than leaving the UI lying.
    const previous = pair.is_watched;
    setPairs((current) =>
      current.map((row) => (row.id === pair.id ? { ...row, is_watched: !previous } : row)),
    );

    try {
      if (previous) {
        // The list carries no watchlist item id, so resolve it from the pair.
        const detail = await api.get<{ watchlist: { id: string } | null }>(`/pairs/${pair.id}`);
        if (detail.watchlist) {
          await api.delete(`/watchlist/${detail.watchlist.id}`);
        }
      } else {
        await api.post('/watchlist', { pair_id: pair.id });
      }
    } catch {
      setPairs((current) =>
        current.map((row) => (row.id === pair.id ? { ...row, is_watched: previous } : row)),
      );
      setError(
        previous ? 'Could not remove from the watchlist.' : 'Could not add to the watchlist.',
      );
    } finally {
      setBusyPairId(null);
    }
  }

  // §5.1 — the triage fix, and only when there is genuinely something to
  // promote.
  //
  // Two conditions, both learned the hard way when this shipped as a `.find()`
  // over verdict `qualified` OR `watch`:
  //
  //  1. **`qualified` only.** The first version accepted `watch` and promoted a
  //     pool holding 0.00000015 ETH that scored 70 — while two genuine 100s sat
  //     further down the list. Promoting the least-bad of a bad batch is worse
  //     than promoting nothing, because it manufactures a recommendation the data
  //     does not support.
  //
  //  2. **The best, not the first.** `.find()` returns list order, which is
  //     recency. "Worth a second look" has to mean the strongest candidate, not
  //     the most recent acceptable one.
  const featured = pairs
    .filter(
      (pair) =>
        pair.risk !== null &&
        pair.risk.complete &&
        !pair.risk.has_veto &&
        pair.risk.verdict === 'qualified',
    )
    .sort((a, b) => (b.risk?.score ?? 0) - (a.risk?.score ?? 0))[0];
  const rest = featured ? pairs.filter((pair) => pair.id !== featured.id) : pairs;
  const freshCount = pairs.filter((pair) => freshIds.has(pair.id)).length;

  return (
    <div className="stack">
      <div className="screen-head">
        <h1 className="screen-title">Launches &amp; liquidity</h1>
        <div className="screen-actions">
          {freshCount > 0 && (
            <span className="chip" data-tone="neutral" aria-live="polite">
              <span className="live-dot" data-live="true" aria-hidden="true" />
              {freshCount} new since you opened this
            </span>
          )}
          <span className="label label-dim">
            {total} pair{total === 1 ? '' : 's'}
          </span>
        </div>
      </div>

      {/* v2: explains the 4 dots every row now carries. One legend for the
          whole feed, not repeated per row. The dots here just mark position
          (which fact is which) -- the trailing text explains what the
          colours mean, since any one dot could be any of the 4 tones
          depending on the pair. */}
      <div className="status-legend">
        <span>Row status dots:</span>
        <span className="status-legend-item">
          <span className="status-dot" aria-hidden="true" /> lifecycle
        </span>
        <span className="status-legend-item">
          <span className="status-dot" aria-hidden="true" /> approval-eligible
        </span>
        <span className="status-legend-item">
          <span className="status-dot" aria-hidden="true" /> reputation
        </span>
        <span className="status-legend-item">
          <span className="status-dot" aria-hidden="true" /> provenance risk
        </span>
        <span>— green good, gold caution, red bad, gray unscored</span>
      </div>

      {/* Phase 11, Slice 11F: lifecycle-status triage tabs. Candidates is
          the default and, until Slice 11G ships, an interim definition --
          see this file's own header comment. */}
      <div className="row" role="tablist" aria-label="Pair lifecycle" style={{ flexWrap: 'wrap', gap: 'var(--s-2)' }}>
        {LIFECYCLE_TABS.map(([value, label]) => (
          <button
            key={value}
            type="button"
            role="tab"
            className="chip"
            data-tone={lifecycleTab === value ? 'neutral' : 'muted'}
            aria-selected={lifecycleTab === value}
            onClick={() => setLifecycleTab(value)}
            style={{
              cursor: 'pointer',
              borderColor: lifecycleTab === value ? 'var(--border-strong)' : undefined,
              background: lifecycleTab === value ? 'var(--bg-raised)' : undefined,
            }}
          >
            {label}
          </button>
        ))}
      </div>

      {/* Standalone filters, composable with any tab above. */}
      <div className="row" style={{ flexWrap: 'wrap', gap: 'var(--s-2)' }}>
        {STANDALONE_FILTER_TOGGLES.map(([stateKey, , label]) => (
          <button
            key={stateKey}
            type="button"
            className="chip"
            data-tone={standaloneFilters[stateKey] ? 'neutral' : 'muted'}
            aria-pressed={standaloneFilters[stateKey]}
            onClick={() =>
              setStandaloneFilters((current) => ({
                ...current,
                [stateKey]: !current[stateKey],
              }))
            }
            style={{
              cursor: 'pointer',
              borderColor: standaloneFilters[stateKey] ? 'var(--border-strong)' : undefined,
              background: standaloneFilters[stateKey] ? 'var(--bg-raised)' : undefined,
            }}
          >
            {label}
          </button>
        ))}
      </div>

      {/* Verdict filters use the verdict palette. Never armed or safed: which
          pairs you are looking at is not a money-can-move fact. */}
      <div className="row" style={{ flexWrap: 'wrap', gap: 'var(--s-2)' }}>
        {VERDICT_FILTERS.map(([value, label]) => (
          <button
            key={value || 'all'}
            type="button"
            className="chip"
            data-verdict={value === 'unscored' || value === '' ? undefined : value}
            data-tone={verdict === value ? 'neutral' : 'muted'}
            aria-pressed={verdict === value}
            onClick={() => setVerdict(value)}
            style={{
              cursor: 'pointer',
              borderColor: verdict === value ? 'var(--border-strong)' : undefined,
              background: verdict === value ? 'var(--bg-raised)' : undefined,
            }}
          >
            {label}
          </button>
        ))}

        <span className="spacer" />

        <input
          className="input data-sm"
          style={{ height: '34px', maxWidth: '240px' }}
          type="search"
          placeholder="symbol or address"
          aria-label="Search pairs by symbol or address"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
        />

        <select
          className="input data-sm"
          style={{ height: '34px', width: 'auto' }}
          aria-label="Filter by status"
          value={status}
          onChange={(event) => setStatus(event.target.value as StatusFilter)}
        >
          <option value="">Any status</option>
          <option value="new">New</option>
          <option value="tracking">Tracking</option>
          <option value="ignored">Ignored</option>
          <option value="bought">Bought</option>
          <option value="error">Error</option>
          <option value="rugged">Rugged</option>
        </select>

        <select
          className="input data-sm"
          style={{ height: '34px', width: 'auto' }}
          aria-label="Filter by quote asset"
          value={quoted}
          onChange={(event) => setQuoted(event.target.value as QuotedFilter)}
        >
          <option value="any">Any quote</option>
          <option value="known">Known quote</option>
          <option value="unknown">No known quote</option>
        </select>
      </div>

      {error && (
        <p className="notice" data-tone="alarm" role="alert">
          {error}
        </p>
      )}

      {loading ? (
        <p className="notice">Loading pairs…</p>
      ) : pairs.length === 0 ? (
        <div className="empty">
          <div className="empty-mark" aria-hidden="true" />
          <p className="empty-title">Nothing discovered yet</p>
          <p className="empty-body">
            The scanner watches the configured factories, and pairs appear here as
            they are created. If this stays empty, check the scanner is running on
            the Bot screen — an idle scanner and an idle chain look identical from
            here.
          </p>
        </div>
      ) : (
        <>
          {featured && (
            <Link
              to={`/pairs/${featured.id}`}
              className="featured"
              data-verdict={featured.risk?.verdict}
            >
              <span className="score-ring" data-verdict={featured.risk?.verdict}>
                <span style={{ textAlign: 'center' }}>
                  <span className="score-ring-value">{featured.risk?.score}</span>
                  <br />
                  <span className="score-ring-of">/100</span>
                </span>
              </span>

              <span className="row-main">
                <span className="featured-flag">Worth a second look</span>
                <span className="featured-name">
                  <PairSymbols pair={featured} />
                  <SafetyChips token={baseTokenOf(featured)} variant="compact" />
                </span>
                <span className="row-meta">
                  <span className="row-fact">
                    {formatEth(featured.liquidity_eth)} ETH liquidity
                  </span>
                  <span className="row-fact">{relativeAge(featured.first_seen_at)} old</span>
                  <StatusDots pair={featured} />
                </span>
              </span>

              <span className="chip" data-verdict={featured.risk?.verdict}>
                {featured.risk?.verdict}
              </span>
            </Link>
          )}

          <div className="rows">
            {rest.map((pair) => (
              <PairRow
                key={pair.id}
                pair={pair}
                onToggleWatch={(target) => void toggleWatch(target)}
                busy={busyPairId === pair.id}
                fresh={freshIds.has(pair.id)}
              />
            ))}
          </div>

          {nextCursor && (
            <button
              className="btn"
              type="button"
              onClick={() => void loadMore()}
              disabled={loadingMore}
              style={{ alignSelf: 'center' }}
            >
              {loadingMore ? 'Loading…' : 'Load more'}
            </button>
          )}
        </>
      )}
    </div>
  );
}